import axios, { AxiosInstance, AxiosError, AxiosRequestHeaders, InternalAxiosRequestConfig } from 'axios';
import i18n from 'i18next';
import { AuthDependencyContainer } from '@/auth/infrastructure/dependencies/AuthDependencyContainer';
import { errorService } from './errorService';

// Crear la instancia de Axios
import { environment } from '@/config/environment';

const API_BASE_URL = environment.apiUrl;

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// -----------------------------------------------------------------------------
// INTERCEPTOR DE REQUEST (JWT - Token fresco via AuthRepository)
// -----------------------------------------------------------------------------

api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Obtener token fresco a través del repositorio (desacoplado del proveedor de auth)
    const authRepository = AuthDependencyContainer.getAuthRepository();

    try {
      const token = await authRepository.getCurrentToken();

      if (token) {
        config.headers = config.headers || {} as AxiosRequestHeaders;
        (config.headers as any).Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error obteniendo token:', error);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// -----------------------------------------------------------------------------
// INTERCEPTOR DE RESPUESTA (Manejo Centralizado de Errores)
// -----------------------------------------------------------------------------

api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    // Las requests canceladas (AbortController) no son errores reales — ignorar
    if (axios.isCancel(error)) return Promise.reject(error);

    // 403 con errores específicos: emitir evento sin redirigir ni desloguear.
    // Se marca el error como `handledByInterceptor` para que los catch genéricos de la
    // capa de aplicación (ej. useTasks.setError) NO pinten además un banner de error rojo:
    // el aviso ya lo da el toast (org-inactive / plan-limit-reached).
    if (error.response?.status === 403) {
      const data = error.response?.data as Record<string, unknown> | undefined;
      if (data?.error === 'ORG_INACTIVE') {
        // R22/OQ4
        (error as AxiosError & { handledByInterceptor?: boolean }).handledByInterceptor = true;
        window.dispatchEvent(new CustomEvent('org-inactive'));
        return Promise.reject(error);
      }
      if (data?.error === 'PLAN_LIMIT_REACHED') {
        // R14: sin redirigir ni desloguear — notificar por recurso
        (error as AxiosError & { handledByInterceptor?: boolean }).handledByInterceptor = true;
        window.dispatchEvent(new CustomEvent('plan-limit-reached', { detail: { blockedBy: data.blockedBy } }));
        return Promise.reject(error);
      }
    }

    // Delega el manejo de errores (401, 403, 500) al servicio centralizado
    errorService.handleApiError(error);
    return Promise.reject(error);
  }
);

/**
 * Indica si un error ya fue gestionado por el interceptor de respuesta (toast de
 * org-inactive / plan-limit-reached). Los catch genéricos deben usarlo para NO
 * pintar además un banner de error rojo redundante.
 */
export function isHandledByInterceptor(error: unknown): boolean {
  return Boolean((error as { handledByInterceptor?: boolean })?.handledByInterceptor);
}

/**
 * Extrae la señal de negocio de un error de axios (E93): SIEMPRE `err.response.data.error`
 * (o `.message`) del backend, NUNCA `err.message` — que en axios es la cadena técnica
 * "Request failed with status code N" y no debe mostrarse al usuario. Devuelve `undefined`
 * cuando no hay señal de negocio, para que el llamante aplique su propio fallback.
 */
export function extractApiErrorMessage(error: unknown): string | undefined {
  const e = error as { response?: { data?: { error?: string; message?: string } } } | undefined;
  return e?.response?.data?.error ?? e?.response?.data?.message;
}

/**
 * Único punto autorizado para convertir un error de API en el texto que ve el usuario (D1-D2).
 * Precedencia: interceptor ya gestionado → null (el toast ya avisó) · código traducido en
 * `errors.<CODE>.description` · prosa de `data.message` · clave genérica `fallbackKey`.
 * Nunca `error.message` — en axios es siempre la cadena técnica "Request failed with status
 * code N" (E93). Usa el singleton de i18next (no `i18n/config.ts`, que dispara `init()` como
 * efecto secundario) porque varios consumidores no montan `useTranslation()`.
 */
export function resolveApiErrorMessage(error: unknown, fallbackKey: string): string | null {
  if (isHandledByInterceptor(error)) return null;

  const data = (error as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
  const fallback = data?.message ?? i18n.t(fallbackKey);

  if (!data?.error) return fallback;
  return i18n.t(`errors.${data.error}.description`, { defaultValue: fallback });
}

export { api };
